/**
 * #2663 — a single-tenant client sees the portal in their own firm's brand.
 *
 * <p>The brand rides on `/me` (`tenant_theme`, the sole tenant's `subject_theme` through the
 * redacted projection — the same brand the client forms wear). The portal wears it only when ALL
 * of these hold, and otherwise looks exactly as it always has:</p>
 * <ul>
 *   <li>`/me` carries a theme — the engine sends one only to a principal who is not a platform
 *       admin and has exactly one allowed tenant, and only when that tenant has a theme;</li>
 *   <li>the principal cannot switch tenant and is not a platform admin — checked again here, so a
 *       multi-tenant viewer never wears any firm's brand, whichever tenant is selected;</li>
 *   <li>the colours derive (the primary is a hex).</li>
 * </ul>
 *
 * <p>The colours are not chosen here: `derivePortalTokens` (the design system's shared brand
 * derivation) maps the firm's inputs onto the tokens the portal's stylesheets already read, and
 * this module only sets them on <html> and takes every one of them off again.</p>
 */
import { useEffect, useMemo } from "react";
import { derivePortalTokens, type PortalTokens } from "@ardept/design-system/brandTokens";
import { useMe } from "../api/portalQueries.js";
import { canSwitchTenant, tenantSummary } from "../api/tenantSelection.js";
import type { MeResponse } from "../api/types.js";

/** What the branded portal shows and wears. `null` everywhere means "the AR Dept. portal". */
export interface TenantBrand {
  /** The CSS custom properties set on <html> while the brand is worn. */
  tokens: PortalTokens;
  /** The firm's name: the theme's own, else the tenant's name from `/me`. */
  name: string;
  /** The logo's address, only when it passed {@link safeLogoUrl}; else the name shows instead. */
  logoUrl: string | null;
  /**
   * The firm's favicon, worn as the tab icon exactly as the client forms wear it — only when it
   * passed the same scheme check as the logo; else the portal keeps its own icon.
   */
  faviconUrl: string | null;
}

/**
 * The logo address, or null. Only an inline image (`data:image/…`) or an `https:` address is ever
 * rendered — the engine filters the same way, and this is the second check, at the point of use.
 * It is only ever an `<img src>`: never a CSS `url()`, never markup.
 */
export function safeLogoUrl(value: string | undefined): string | null {
  if (typeof value !== "string") return null;
  return /^(?:data:image\/|https:)/i.test(value) ? value : null;
}

/** The brand this principal's portal wears, or null for the AR Dept. portal. Pure. */
export function tenantBrand(me: MeResponse | undefined): TenantBrand | null {
  const theme = me?.tenant_theme;
  if (!me || !theme) return null;
  if (me.is_platform_admin || canSwitchTenant(me)) return null;

  const tokens = derivePortalTokens({
    primary: theme.primary,
    secondary: theme.secondary ?? theme.primary,
    accent: theme.accent,
    surface_page: theme.surface_page,
    surface_card: theme.surface_card,
    text_primary: theme.text_primary,
    text_secondary: theme.text_secondary,
  });
  if (!tokens) return null;

  const ownName = theme.display_name?.trim();
  const name = ownName || (tenantSummary(me, me.allowed_tenant_ids[0] ?? null)?.display_name ?? "");
  return {
    tokens,
    name,
    logoUrl: safeLogoUrl(theme.logo_url),
    faviconUrl: safeLogoUrl(theme.favicon_url),
  };
}

/**
 * Wear `href` as the document's tab icon (`<link rel="icon">`), the way the client forms do
 * (subject-surface FormExperience `applyTheme`). Returns the undo: the previous icon's address is
 * put back, or the link this call created is removed, so the portal's own icon returns the moment
 * the brand stops applying.
 */
export function applyFavicon(doc: Document, href: string): () => void {
  let icon = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
  const created = icon === null;
  const previous = icon?.getAttribute("href") ?? null;
  if (icon === null) {
    icon = doc.createElement("link");
    icon.setAttribute("rel", "icon");
    doc.head.appendChild(icon);
  }
  icon.setAttribute("href", href);
  const worn = icon;
  return () => {
    if (created) worn.remove();
    else if (previous === null) worn.removeAttribute("href");
    else worn.setAttribute("href", previous);
  };
}

/** {@link useTenantBrand}'s answer: whose brand the portal wears, once that is known. */
export interface TenantBrandState {
  /** The firm's brand, or null for the AR Dept. portal. Always null while {@link pending}. */
  brand: TenantBrand | null;
  /**
   * `/me` has not answered yet (no data and no error), so whose brand this is is not known: the
   * nav shows no mark at all rather than the AR Dept.'s and then a swap to the firm's. A FAILED
   * `/me` is not pending — it answers "the AR Dept. portal", so the error state stays recognisable.
   */
  pending: boolean;
}

/**
 * The brand for the signed-in principal, worn on <html> for as long as it applies.
 *
 * <p>Call it ONCE, at the shell. Every property it sets is removed again when the brand stops
 * applying (a `/me` that no longer qualifies, a sign-out) or the shell unmounts, so the token
 * file's AR Dept. values are back the moment the brand is not.</p>
 */
export function useTenantBrand(): TenantBrandState {
  const me = useMe();
  const brand = useMemo(() => tenantBrand(me.data), [me.data]);

  useEffect(() => {
    if (!brand) return;
    const root = document.documentElement;
    const names = Object.keys(brand.tokens) as Array<keyof PortalTokens>;
    for (const name of names) root.style.setProperty(name, brand.tokens[name]);
    const restoreFavicon = brand.faviconUrl ? applyFavicon(document, brand.faviconUrl) : null;
    return () => {
      for (const name of names) root.style.removeProperty(name);
      restoreFavicon?.();
    };
  }, [brand]);

  return { brand, pending: me.isPending };
}
